/* eslint-disable no-use-before-define */
import { FC, PropsWithChildren } from 'react';
import '@screentone/addon-autocomplete/css/index.css';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { AutoComplete } from '@screentone/addon-autocomplete';
import {
  Button,
  Checkbox,
  Divider,
  FormLabel,
  Group,
  IconArrowLeft,
  IconArrowRight,
  Loader,
  Radio,
  Textarea,
  Typography
} from '@screentone/core';
import classNames from 'classnames';
import { format } from 'date-fns-tz';
import { JsonData, JsonEditor } from 'json-edit-react';

import { HelpIcon } from 'components';
import { ContentParametersBulkUpdateMode, ParameterRange } from 'data/generated/graphql';
import { useAuraQuery } from 'hooks';
import { AuraEmployeeResponse } from 'hooks/useAuraQuery';
import { DATE_TIME_FORMAT, FORBIDDEN_KEYS, OPTIONAL_PARAMETER_KEYS } from './constants';
import { useMappingContext } from './MappingContext';
import styles from './MappingView.module.scss';
import { EDITOR_ENUMS, getUserText, ON_ADD } from './utils';

export const MappingViewHeader: FC = () => {
  const { updatedUtc, createdUtc, creatorUser, revisorUser, currentHistoryIndex, history } = useMappingContext();
  const { currentProperty } = useAuth();
  let creatorUserIdValue = creatorUser.djUserId;
  let createdUtcValue = createdUtc;

  if (currentHistoryIndex !== null) {
    const historyItem = history![currentHistoryIndex];

    creatorUserIdValue = historyItem.creatorUser.djUserId;
    createdUtcValue = historyItem.createdUtc;
  }

  const { data: creatorUserData, isLoading: isFetchingCreatorUserData } = useAuraQuery(creatorUserIdValue, {
    enabled: !!creatorUser,
    currentProperty
  });

  const revisorUserId = revisorUser?.djUserId ?? '';

  const { data: revisorUserData, isLoading: isFetchingRevisorUserData } = useAuraQuery(revisorUserId, {
    enabled: !!revisorUser,
    currentProperty
  });

  const creatorUserText = getUserText(creatorUserData as AuraEmployeeResponse | undefined, creatorUser.djUserId);
  const revisorUserText = getUserText(revisorUserData as AuraEmployeeResponse | undefined, revisorUserId);

  return (
    <Group fullWidth>
      <Group.Item>
        <Typography variant="label2">Created by</Typography>
        <Typography variant="label1">{isFetchingCreatorUserData ? <Loader size="md" /> : creatorUserText}</Typography>
      </Group.Item>
      <Group.Item>
        <Typography variant="label2">Created at</Typography>
        <Typography variant="label1">{format(createdUtcValue, DATE_TIME_FORMAT)}</Typography>
      </Group.Item>
      {revisorUserId && currentHistoryIndex === null && (
        <Group.Item>
          <Typography variant="label2">Last updated by</Typography>
          <Typography variant="label1">{isFetchingRevisorUserData ? <Loader size="md" /> : revisorUserText}</Typography>
        </Group.Item>
      )}
      {updatedUtc && currentHistoryIndex === null && (
        <Group.Item>
          <Typography variant="label2">Last updated on</Typography>
          <Typography variant="label1">{format(updatedUtc, DATE_TIME_FORMAT)}</Typography>
        </Group.Item>
      )}
    </Group>
  );
};

MappingViewHeader.displayName = 'MappingViewHeader';

type ContentParametersEditorChildComponents = {
  DeprecatedField: typeof DeprecatedField;
  JSONEditor: typeof JSONEditor;
  CommentField: typeof CommentField;
};

// ContentParametersEditor component with sub-components
interface ContentParametersEditorProps {}
export const ContentParametersEditor: FC<PropsWithChildren<ContentParametersEditorProps>> &
  ContentParametersEditorChildComponents = ({ children }) => <>{children}</>;

// Sub-component: DeprecatedField
interface DeprecatedFieldProps {
  viewOnly?: boolean;
}
const DeprecatedField: FC<DeprecatedFieldProps> = ({ viewOnly }) => {
  const { deprecated, treatmentType, dispatch } = useMappingContext();

  const handleDeprecatedChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'UPDATE_DEPRECATED', payload: { treatmentType, value: changeEvent.target.checked } });
  };

  return (
    <Group fullWidth>
      <Group.Item>
        <Typography variant="label2">Deprecated</Typography>
        <Checkbox
          name="checkbox"
          id="checkbox"
          checked={deprecated}
          onChange={handleDeprecatedChange}
          disabled={viewOnly}
        />
      </Group.Item>
    </Group>
  );
};

// Sub-component: JSONEditor
interface JSONEditorProps {
  viewOnly?: boolean;
  parameters: ParameterRange[];
  treatmentType: string;
  dispatch: React.Dispatch<any>;
  disabled?: boolean;
}
const JSONEditor: FC<PropsWithChildren<JSONEditorProps>> = ({
  children,
  viewOnly,
  parameters,
  treatmentType,
  dispatch,
  disabled
}) => {
  const handleParametersChange = (value: JsonData) => {
    dispatch({ type: 'UPDATE_PARAMETERS', payload: { treatmentType, value: value as ParameterRange[] } });
  };

  return (
    <Group fullWidth>
      <Group.Item fullWidth>
        <Group wrap={false} gap="sm" valign="center">
          <Typography variant="label2">Parameters</Typography>
          {children}
        </Group>
        <JsonEditor
          data={parameters.map((range) => {
            const value = range.value as Record<string, unknown>;
            const filteredValue = Object.fromEntries(
              Object.entries(value).filter(([k, v]) => !OPTIONAL_PARAMETER_KEYS.includes(k) || v != null)
            );
            return { ...range, value: filteredValue };
          })}
          showIconTooltips
          showCollectionCount="when-closed"
          indent={8}
          maxWidth="100%"
          setData={handleParametersChange}
          onAdd={ON_ADD}
          restrictDelete={({ key }) => FORBIDDEN_KEYS.includes(key as string)}
          restrictAdd={({ level, key }) => level === 1 || key === 'value'}
          showArrayIndices
          viewOnly={viewOnly ?? disabled}
          restrictDrag
          restrictTypeSelection={EDITOR_ENUMS}
        />
      </Group.Item>
    </Group>
  );
};

// Sub-component: CommentField
interface CommentFieldProps {
  viewOnly?: boolean;
}
const CommentField: FC<CommentFieldProps> = ({ viewOnly }) => {
  const { comment, treatmentType, dispatch } = useMappingContext();

  const handleCommentChange = (changeEvent: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({ type: 'UPDATE_COMMENT', payload: { treatmentType, value: changeEvent.target.value } });
  };

  return (
    <Group fullWidth>
      <Group.Item fullWidth>
        <Typography variant="label2">Comment</Typography>
        <Textarea
          name="textarea"
          id="textarea-comment"
          placeholder="Write a comment about this mapping if needed"
          value={comment}
          onChange={handleCommentChange}
          disabled={viewOnly}
        />
      </Group.Item>
    </Group>
  );
};

ContentParametersEditor.DeprecatedField = DeprecatedField;
ContentParametersEditor.JSONEditor = JSONEditor;
ContentParametersEditor.CommentField = CommentField;

export const BulkUpdateControls: FC = () => {
  const { treatmentType, bulkUpdateMode, bulkUpdateArtifactNames, dispatch, artifactsPerTreatment } =
    useMappingContext();

  const handleBulkUpdateModeChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    if (changeEvent.target.value === 'none-mode') {
      dispatch({
        type: 'BULK_UPDATE_SET_MODE',
        payload: { treatmentType, mode: ContentParametersBulkUpdateMode.None }
      });
    } else if (changeEvent.target.value === 'include-mode') {
      dispatch({
        type: 'BULK_UPDATE_SET_MODE',
        payload: { treatmentType, mode: ContentParametersBulkUpdateMode.Include }
      });
    } else if (changeEvent.target.value === 'exclude-mode') {
      dispatch({
        type: 'BULK_UPDATE_SET_MODE',
        payload: { treatmentType, mode: ContentParametersBulkUpdateMode.Exclude }
      });
    }
  };

  const handleBulkUpdateAllesehIds = (tags: string[]) => {
    dispatch({ type: 'BULK_UPDATE_SET_ALLESSEH_IDS', payload: { treatmentType, artifactNames: tags } });
  };

  return (
    <>
      <Divider fullWidth />
      <Typography variant="label2" margin={{ bottom: 'none' }}>
        Update mode
      </Typography>
      <Group wrap={false}>
        <Group wrap={false} gap="sm" valign="baseline">
          <FormLabel label="All" labelPosition="right" fullWidth>
            <Radio
              checked={bulkUpdateMode === ContentParametersBulkUpdateMode.None}
              onChange={handleBulkUpdateModeChange}
              value="none-mode"
            />
          </FormLabel>
          <HelpIcon isLeftAnchored text="On All mode, all sections that use this treatment type will be updated." />
        </Group>
        <Group wrap={false} gap="sm" valign="baseline">
          <FormLabel label="Include" labelPosition="right" fullWidth>
            <Radio
              checked={bulkUpdateMode === ContentParametersBulkUpdateMode.Include}
              onChange={handleBulkUpdateModeChange}
              value="include-mode"
            />
          </FormLabel>
          <HelpIcon
            isLeftAnchored
            text="On Include mode, only the sections listed in the text area below will be updated."
          />
        </Group>
        <Group wrap={false} gap="sm" valign="baseline">
          <FormLabel label="Exclude" labelPosition="right" fullWidth>
            <Radio
              checked={bulkUpdateMode === ContentParametersBulkUpdateMode.Exclude}
              onChange={handleBulkUpdateModeChange}
              value="exclude-mode"
            />
          </FormLabel>
          <HelpIcon
            isLeftAnchored
            text="On Exclude mode, all sections except for the ones listed in the text area below, will be updated."
          />
        </Group>
      </Group>
      <Group fullWidth>
        <Group.Item fullWidth>
          <AutoComplete
            id="autocomplete-include-exclude"
            dropDownItems={artifactsPerTreatment?.filter((id) => !bulkUpdateArtifactNames?.includes(id)) ?? []}
            dropDownClassName={styles.autocompleteDropdown}
            onDismiss={(tags: string[]) => handleBulkUpdateAllesehIds(tags)}
            onSelect={(tags: string[]) => handleBulkUpdateAllesehIds(tags)}
            tags={bulkUpdateArtifactNames}
            placeholder="Select the list of sections to include or exclude from the update"
          />
        </Group.Item>
      </Group>
    </>
  );
};

BulkUpdateControls.displayName = 'BulkUpdateControls';

interface ContentParametersHistoryProps extends React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {}

export const ContentParametersHistory: FC<PropsWithChildren<ContentParametersHistoryProps>> = ({
  className,
  ...props
}) => {
  const {
    history,
    currentHistoryIndex,
    actions: {
      history: { handleNext, handlePrevious }
    }
  } = useMappingContext();

  const hasHistory = Array.isArray(history) && history.length > 0;
  const isLastHistoryItem = hasHistory && currentHistoryIndex === history.length - 1;

  return (
    <Group {...props} className={classNames(className)} align="space-between" valign="center" fullWidth>
      <Button
        tertiary
        iconPosition="left"
        icon={IconArrowLeft}
        disabled={!hasHistory || isLastHistoryItem}
        onClick={handlePrevious}
      >
        Previous
      </Button>
      <Group gap="xs">
        <Typography
          margin={{
            all: 'none'
          }}
          variant="label2"
        >
          {currentHistoryIndex === null ? 'Current Mapping' : `History Item ${currentHistoryIndex + 1}`}
        </Typography>
        <HelpIcon text="Only the last 50 items are saved in history" />
      </Group>
      <Button
        tertiary
        icon={IconArrowRight}
        iconPosition="right"
        onClick={handleNext}
        disabled={!hasHistory || currentHistoryIndex === null}
      >
        Next
      </Button>
    </Group>
  );
};

ContentParametersHistory.displayName = 'ContentParametersHistory';
